<template>
    <div>
        <table class="table table-condensed cartTable">
            <thead>
                <tr>
                    <th width="10%" class="hidden-xs">&nbsp;</th>
                    <th width="48%">
                        {{labels[996]}}<!-- Item -->
                    </th>
                    <th v-if="canSeePrices" width="12%" class="price">
                        {{labels[997]}}<!-- Item Price -->
                    </th>
                    <th width="10%" class="qtybox text-right">
                        {{labels[998]}}<!-- Quantity -->
                    </th>
                    <th v-if="canSeePrices" width="20%" class="price">
                        {{labels[999]}}<!-- Item Total -->
                    </th>
                </tr>
            </thead>
            <template v-if="cart.itemList.length">
                <CartItem
                    v-for="(item, itemIndex) in cart.itemList.slice().reverse()"
                    :reverse-item-index="itemIndex"
                    :key="itemIndex">
                </CartItem>
            </template>
            <tbody v-else class="empty">
                <tr>
                    <td width="100%" :colspan="(canOrder ? '5' : '3')">
                        <template v-if="canOrder">
                            {{labels[1006]}}<!-- Your shopping cart is empty.  -->
                        </template>
                        <template v-else>
                            {{labels[1007]}}<!-- Your quote list is empty. -->
                        </template>
                        {{labels[1008]}}<!-- Add products by visiting product pages. -->
                    </td>
                </tr>
            </tbody>
            <tfoot>
                <tr v-if="cart.itemList.length" class="total">
                    <td class="hidden-xs">&nbsp;</td>
                    <td width="480px">
                    </td>
                    <td>
                    </td>
                    <template v-if="canSeePrices">
                        <td class="text-right">{{labels[1009]}}<!-- Total -->:</td>
                        <td class="cartTotal price">
                            <template v-if="!loadingCartTotal">
                                <template v-if="!hasSpecial">
                                    {{getCartTotal}}
                                </template>
                                <template v-else>
                                    {{labels[1942]}}<!-- label: n/a -->
                                </template>
                            </template>
                            <template v-else>
                                <img src='/layouts/main/styles/images/spinner.gif' />
                            </template>
                        </td>
                    </template>
                    <td v-else></td>
                </tr>
            </tfoot>
        </table>
        <div v-if="cart.itemList.length" class="row botrow">
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                <p v-if="showStopProgressMsg" class="alert alert-danger icon">
                    {{labels[1010]}}<!-- Please remove the items flagged above to continue. -->
                </p>
                <p v-if="configedItems" class="textleft">
                    <span class="text-danger"><strong>*</strong></span> {{labels[1012]}}<!-- Verify that configurable items meet your project requirements. -->
                </p>
                <!-- Invalid Product Warning Message -->
                <p v-if="hasInvalid" class="alert alert-warning icon">
                    The item(s) flagged above have a configuration problem. Please remove them or edit the configuration to continue. <!-- label: The item(s) flagged above have a configuration problem. Please remove them or edit the configuration to continue. -->
                </p>
                <div class="alert alert-warning">
                    <strong>Note: Our ordering process has changed.</strong> Please note that quantity and customer-specific discounts will not show in your cart, but they will be reflected on your final Order Confirmation. We appreciate your patience as we implement these updates.
                </div>
            </div>
            <div class="col-lg-6 col-md-6 col-sm-6 col-xs-12">
                <template v-if="canOrder">
                    <component :is="checkoutDisabled == 'disable' ? 'a' : 'router-link'" rel="nofollow" :to="{name: 'Ship Address'}" :class="checkoutDisabled" class="btn btn-primary btn-block">
                        {{labels[1013]}} <span>&rsaquo;</span><!-- Checkout -->
                    </component>
                    <br>
                </template>
                <a class="btn btn-primary btn-block" :class="quoteDisabled" href="/request-quote">
                    {{labels[1014]}} <span>&rsaquo;</span><!-- Request Quote -->
                </a>
                <br class="visible-xs">
            </div>
        </div>
    </div>
</template>

<script>
import useCart from "../composables/useCart"
import useCMS from "../../shared/composables/useCMS"
import { ref, computed } from '@vue/composition-api'
import CartItem from '../components/CartItem.vue'

export default {
    name: 'CartTable',
    components: {
        CartItem
    },
    setup () {
        const { cart, canOrder, isLoggedIn, canSeePrices, isEcommerce, empCantOrder, hasOrderData } = useCart();
        const { labels } = useCMS();
        const { hasSpecial, hasInvalid, stopProgress, hasConfigedItems } = useProductFlags(canOrder, cart);

        const checkoutDisabled = computed(() => (stopProgress.value || hasInvalid.value || (empCantOrder.value && isEcommerce.value)) ? 'disable' : '');
        const quoteDisabled = computed(() => (hasInvalid.value) ? 'disable' : '');
        const getCartTotal = computed(() => cart && cart.value.pricing && cart.value.pricing.formattedTotal ? cart.value.pricing.formattedTotal : 0);
        const configedItems = computed(() => hasOrderData.value && hasConfigedItems.value);
        const showStopProgressMsg = computed(() => stopProgress.value && isLoggedIn.value);

        //check if any of the cart items have loadingTotal = true
        const loadingCartTotal = computed(() => cart.value.itemList.findIndex(el => el.loadingTotal) !== -1);

        return {
            cart,
            canOrder,
            canSeePrices,
            isEcommerce,
            hasSpecial,
            hasInvalid,
            showStopProgressMsg,
            checkoutDisabled,
            quoteDisabled,
            getCartTotal,
            configedItems,
            loadingCartTotal,
            labels
        }
    }
}

function useProductFlags (canOrder, cart) {
    const parseProductFlags = () => {
        let configed = ref(false);
        let invalid = ref(false);
        let stopProgress = ref(false);
        let special = ref(false);

        if (cart.value.itemList) {
            for (let i = 0; i < cart.value.itemList.length; i++) {
                if (cart.value.itemList[i].invalid) {
                    invalid.value = true;
                }

                switch(cart.value.itemList[i].type) {
                    case 'sysconfig':
                        if (canOrder.value && !cart.value.itemList[i].sysInfo.prod_purchase) {
                            stopProgress.value = true;
                        }
                        break;
                    case 'sysBuilder':
                    case 'subscription':
                        if (canOrder.value && !cart.value.itemList[i].canPurchaseOnline) {
                            stopProgress.value = true;
                        }
                        break;
                    default:
                        if (cart.value.itemList[i].products.length && cart.value.itemList[i].products[0].configurable) {
                            configed.value = true;
                        }
                        if (canOrder.value && !cart.value.itemList[i].canPurchaseOnline) {
                            stopProgress.value = true;
                        } else if (!canOrder.value && !cart.value.itemList[i].quote) {
                            stopProgress.value = true;
                        }
                        if (cart.value.itemList[i].products.length && cart.value.itemList[i].products[0].special) {
                            special.value = true;
                        }
                }
            }
        }
        return {
            configed: configed,
            invalid: invalid,
            special: special,
            stopProgress: stopProgress
        }
    }

    let flags = parseProductFlags();

    const hasSpecial = computed(() => flags.special.value); //need to loop through products to see if has special is true
    const hasInvalid = computed(() => flags.invalid.value);
    const stopProgress = computed(() => flags.stopProgress.value);
    const hasConfigedItems = computed(() => flags.configed.value);

    return {
        hasSpecial,
        hasInvalid,
        stopProgress,
        hasConfigedItems
    }
}
</script>
